import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { Yacht } from "@/models/Yacht";

export const useComparisonStore = defineStore("comparison", () => {

    const yachtsToCompare = ref<Array<{
        yacht: Yacht;
        price: number;
        isDeal: boolean;
        finalPrice: number;
        discount: number;
        location: { name: string; latitude: number; longitude: number };
    }>>([]);
    
    const selectedYachts = ref<number[]>([]);

    const count = computed(() => selectedYachts.value.length);

    const isYachtSelected = (id: number) => {
        return selectedYachts.value.includes(id);
    };

    function onCompare(yacht) {
        
        const existingBoatIndex = yachtsToCompare.value.findIndex(
            (compBoat) => compBoat.yacht.id === yacht.yacht.id
        );

        if (existingBoatIndex !== -1) {
            console.log("Removing yacht", yacht.yacht.id);
            yachtsToCompare.value.splice(existingBoatIndex, 1);
        } else {
            if (yachtsToCompare.value.length < 4) {

                const yachtData = {
                    yacht: yacht.yacht,
                    price: yacht.price,
                    finalPrice: yacht.finalPrice,
                    isDeal: yacht.isDeal,
                    discount: yacht.discount[0],
                    location: {
                        name: yacht.locationFrom.name,
                        latitude: yacht.locationFrom.latitude,
                        longitude: yacht.locationFrom.longitude,
                    },
                };

                yachtsToCompare.value.push(yachtData);
            } else {
                console.warn("Maximum of 4 yachts can be compared.");
            }
        }
        
        localStorage.setItem('yachtsToCompare', JSON.stringify(yachtsToCompare.value));
    }

    const findYachtById = (id: number) => {
        return yachtsToCompare.value.find((compYacht) => compYacht.yacht.id === id) || null;
    };

    const addYacht = (yacht: { yacht: Yacht; price: number; finalPrice: number }) => {
        if (!yacht || !yacht.yacht || !yacht.price || !yacht.finalPrice) {
            console.warn("Invalid yacht data provided.");
            return;
        }
        if (!yachtsToCompare.value.some((y) => y.yacht.id === yacht.yacht.id)) {
            yachtsToCompare.value.push(yacht);
            saveToLocalStorage();
        }
    };
    const removeYacht = (uuid: number) => {
        yachtsToCompare.value = yachtsToCompare.value.filter((compYacht) => compYacht.yacht.id !== uuid);
        saveToLocalStorage();

        // Return updated IDs list
        return yachtsToCompare.value.map((compYacht) => compYacht.yacht.id);
    };

    const clearComparison = () => {
        yachtsToCompare.value = [];
        selectedYachts.value = [];
        saveToLocalStorage();
    };

    const calculateDiscount = (discount: number) => {
        return Math.round(discount);
    };
    
    const getYachtData = (yachtId: number) => {
        const yachtObj = yachtsToCompare.value.find((compYacht) => compYacht.yacht.id === yachtId);
        if (!yachtObj) {
            console.warn(`Yacht with ID ${yachtId} not found in yachtsToCompare`);
            return { price: 0, finalPrice: 0, isDeal: false };
        }
        return {
            price: yachtObj.price,
            finalPrice: yachtObj.finalPrice,
            isDeal: yachtObj.isDeal,
            discount: yachtObj.discount,
        };
    };

    const saveToLocalStorage = () => {
        localStorage.setItem("yachtsToCompare", JSON.stringify(yachtsToCompare.value));
    };

    const getLocationByYachtId = (yachtId: number) => {
        const yachtObj = yachtsToCompare.value.find(
            (compYacht) => compYacht.yacht.id === yachtId
        );
        return yachtObj.location;
    };
    
    const loadFromLocalStorage = () => {
        const storedYachts = localStorage.getItem("yachtsToCompare");
        if (storedYachts) {
            yachtsToCompare.value = JSON.parse(storedYachts);
        }
    };

    const getStats = (yacht: Yacht, userStore: any) => [
        { key: 'cabins', label: 'boatComparison.stats.cabins', value: yacht.cabins || "-" },
        { key: 'berths', label: 'boatComparison.stats.beds', value: yacht.berths || "-" },
        { key: 'wc', label: 'boatComparison.stats.wc', value: yacht.wc || "-" },
        { key: 'length', label: 'boatComparison.stats.length', value: userStore.formatLength(yacht.length || 0) },
        { key: 'width', label: 'boatComparison.stats.width', value: yacht.width || "-" },
        { key: 'draught', label: 'boatComparison.stats.draught', value: yacht.draught || "-" },
        { key: 'fuelCapacity', label: 'boatComparison.stats.fuelCapacity', value: yacht.fuelCapacity || "-" },
        { key: 'waterCapacity', label: 'boatComparison.stats.waterCapacity', value: yacht.waterCapacity || "-" },
        { key: 'engine', label: 'boatComparison.stats.engine', value: yacht.engine || "-" },
        { key: 'year', label: 'boatComparison.stats.builtYear', value: yacht.year || "-" },
    ];
    

    return {
        yachtsToCompare,
        selectedYachts,
        count,
        isYachtSelected,
        addYacht,
        removeYacht,
        clearComparison,
        calculateDiscount,
        getYachtData,
        findYachtById,
        getStats,
        loadFromLocalStorage,
        saveToLocalStorage,
        onCompare,
        getLocationByYachtId
    };
});